export interface AppConfig {
  environment: string;
  supabase: {
    url: string;
    anonKey: string;
  };
  api: {
    baseUrl: string;
    timeout: number;
  };
  debug: {
    enabled: boolean;
    logLevel: string;
  };
}

let config: AppConfig | null = null;

/** True quand le bundle est construit par Vite (version web). */
function isViteWeb(): boolean {
  return typeof import.meta !== 'undefined' && typeof (import.meta as any).env !== 'undefined';
}

/**
 * Construit la config à partir des variables Vite (import.meta.env.VITE_*).
 * À utiliser uniquement en version web ; retourne null si les variables requises sont absentes.
 */
function getConfigFromViteEnv(): AppConfig | null {
  if (!isViteWeb()) {
    return null;
  }
  const env = (import.meta as any).env as Record<string, string | undefined>;
  const url = env.VITE_SUPABASE_URL;
  const anonKey = env.VITE_SUPABASE_ANON_KEY ?? env.VITE_SUPABASE_PUBLISHABLE_KEY;
  const apiBaseUrl = env.VITE_API_BASE_URL;
  if (!url || !anonKey || !apiBaseUrl) {
    return null;
  }
  const environment = env.VITE_ENVIRONMENT ?? (env.MODE === 'development' ? 'development' : 'production');
  const isDev = environment === 'development';
  return {
    environment,
    supabase: { url, anonKey },
    api: { baseUrl: apiBaseUrl, timeout: 30000 },
    debug: { enabled: isDev, logLevel: isDev ? 'debug' : 'error' },
  };
}

/**
 * Détermine l'environnement selon les variables d'environnement ou le mode de build
 */
function getEnvironment(): string {
  // Vite web : variable explicite
  if (isViteWeb()) {
    const env = (import.meta as any).env as Record<string, string | undefined>;
    if (env.VITE_ENVIRONMENT) return env.VITE_ENVIRONMENT;
    if (env.MODE === 'development') return 'development';
    return 'production';
  }
  // Variable d'environnement explicite (Node / React Native)
  if (typeof process !== 'undefined' && process.env.ENVIRONMENT) {
    return process.env.ENVIRONMENT;
  }

  // Détecter l'environnement : __DEV__ pour React Native, process.env.NODE_ENV pour web
  const isDev =
    (typeof __DEV__ !== 'undefined' && __DEV__) ||
    (typeof process !== 'undefined' && process.env.NODE_ENV === 'development');

  if (isDev) return 'development';
  return 'production';
}

export const loadConfig = async (): Promise<AppConfig> => {
  if (config) {
    return config;
  }

  // Version web (Vite) : config uniquement depuis les variables d'environnement (pas de production.json)
  if (isViteWeb()) {
    const viteConfig = getConfigFromViteEnv();
    if (viteConfig) {
      config = viteConfig;
      if (config.debug?.enabled) {
        console.log(`[Config] Loaded from env (web), environment: ${config.environment}`);
        console.log(`[Config] API URL: ${config.api.baseUrl}`);
        console.log(`[Config] Supabase URL: ${config.supabase.url}`);
      }
      return config;
    }
    throw new Error(
      'Configuration web manquante. Définir les variables d’environnement : VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY (ou VITE_SUPABASE_PUBLISHABLE_KEY), VITE_API_BASE_URL. Optionnel : VITE_ENVIRONMENT (development|staging|production).'
    );
  }

  // React Native : fallback sur les fichiers JSON
  const environment = getEnvironment();
  try {
    const configByEnv: Record<string, AppConfig> = {
      development: require('../assets/config/development.json'),
      production: require('../assets/config/production.json'),
      staging: require('../assets/config/staging.json'),
    };
    const configData = configByEnv[environment];
    if (!configData) {
      throw new Error(`Unknown environment: ${environment}. Supported: development, production, staging.`);
    }

    config = configData;

    if (config.debug?.enabled) {
      console.log(`[Config] Loaded for environment: ${environment}`);
      console.log(`[Config] API URL: ${config.api.baseUrl}`);
      console.log(`[Config] Supabase URL: ${config.supabase.url}`);
    }

    return config;
  } catch (error: any) {
    console.error(`[Config] Failed to load configuration:`, error);
    throw new Error(`Failed to load configuration for environment '${environment}': ${error.message}`);
  }
};

export const getConfig = (): AppConfig => {
  if (!config) {
    throw new Error('Config not loaded. Call loadConfig() first.');
  }
  return config;
};
